import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { COMPARISONS, type Filters, PRESETS, SELECTS, TABS } from './model';

const SOURCE =
  'reporting.cs_daily_metrics (the CS Daily Report rows), cs_daily_team_metrics, contact_centre_contacts, contact_centre_contact_fcr, zendesk_open_book_daily, zendesk_handoff_open, fact_zendesk_ticket; built daily at 03:00 Sydney.';

function selectSchema(options: Record<string, Array<string>>): Record<string, PageField> {
  return Object.fromEntries(SELECTS.map(({ key }) => [key, { type: 'enum', values: [null, ...(options[key] ?? [])] }]));
}

export function csDailyOpsPageView(input: {
  ready: boolean;
  filters: Filters;
  options: Record<string, Array<string>>;
  period: string;
  summary: Record<string, number | null>;
}): PageView {
  const { filters } = input;
  return {
    title: 'CS Daily Ops',
    ready: input.ready,
    dataMode: 'live lux-analytics reporting tables',
    filters: {
      tab: filters.tab,
      period: filters.preset,
      compare: filters.comparison,
      ...filters.selections,
    },
    filterSchema: {
      tab: { type: 'enum', values: TABS.map((tab) => tab.value) },
      period: { type: 'enum', values: PRESETS.map((preset) => preset.value) },
      compare: { type: 'enum', values: COMPARISONS.map((comparison) => comparison.value) },
      ...selectSchema(input.options),
    },
    period: input.period,
    summary: input.summary,
    definitions: {
      source: SOURCE,
      dates:
        'Sydney days. Yesterday, 7 days, this week (Monday to the last complete day), last week (Monday to Sunday), 30 days, this month. Comparison: the previous window of the same length, or the same dates one calendar year back.',
      teams:
        'CS = Support AU, Support ATI and Support INTL (the Zendesk Support groups). Other CS teams = Flight Support, Back of House, Tours and Cruise Concierge, Customer Operations, Wholesale and the retail stores.',
      fcr: 'A human-handled contact is resolved first time when the same customer made no further CS contact on any channel and did not reopen the linked ticket within 7 days; read 7 days back.',
      openBook:
        'Tickets in new / open / pending / hold at 23:59:59, excluding LESA-deflected chats, chat tickets never picked up (new, no owner, no human reply) and API / side-conversation threads.',
    },
  };
}

export function editFromBridge(changes: Record<string, unknown>, view: PageView, filters: Filters): Filters {
  validatePageFilters(changes, view.filterSchema);
  const next: Filters = { ...filters, selections: { ...filters.selections } };
  if ('tab' in changes) {
    next.tab = changes.tab as Filters['tab'];
  }
  if ('period' in changes) {
    next.preset = changes.period as Filters['preset'];
  }
  if ('compare' in changes) {
    next.comparison = changes.compare as Filters['comparison'];
  }
  for (const { key } of SELECTS) {
    if (key in changes) {
      next.selections[key] = (changes[key] as string | null) || null;
    }
  }
  return next;
}
